import { DATA_BASE_URL } from '@lib/constants';
import Icon from '@components/common/Icon';

const ProjectHighlightsSection = () => {
  const projects = [
    {
      id: 'jarvis',
      company: 'Omind',
      title: 'Jarvis - AI Support Agent',
      description: 'An intelligent AI agent designed to help users troubleshoot laptop issues using natural language processing and real-time search capabilities.',
      challenge: 'Users needed quick, accurate technical support for common laptop issues without waiting for human assistance.',
      solution: 'Developed an AI-powered support agent using Python and Google ADK, implementing RAG architecture for context-aware responses and integrating Google APIs for real-time information retrieval.',
      technologies: ['Python', 'Google ADK', 'LLMs', 'RAG', 'Google APIs', 'Prompt Engineering'],
      outcomes: [
        'Reduced average response time for technical queries',
        'Improved accuracy through iterative testing and refinement',
        'Created scalable architecture for future AI agents',
        'Gained hands-on experience with cutting-edge AI technologies',
      ],
      icon: 'robot',
      color: 'var(--color-accent-6)',
      documentation: `${DATA_BASE_URL}/data/internships/omind/omind_project_documentation_2025.pdf`,
    },
    {
      id: 'albertson-app',
      company: 'Albertsons Companies',
      title: 'Self Check-In iPad Application',
      description: 'A Python-based iPad application that revolutionized employee check-in processes at Albertsons retail stores nationwide.',
      challenge: 'Store staff faced delays and inefficiencies when checking in, leading to 100,000+ unnecessary calls to the corporate helpdesk.',
      solution: 'Enhanced existing iPad application with improved user interface and streamlined workflows, enabling staff to self-check-in without helpdesk assistance.',
      technologies: ['Python', 'iOS Development', 'UI/UX Design', 'Retail Systems'],
      outcomes: [
        'Reduced helpdesk calls by 30% (100,000 calls)',
        'Deployed to 2,000+ stores nationwide',
        'Improved staff efficiency and satisfaction',
        'Name embedded in program as permanent recognition',
      ],
      icon: 'mobile-phone',
      color: 'var(--color-accent-5)',
    },
    {
      id: 'honda-robotics',
      company: 'SRP Technologies (Honda)',
      title: 'Industrial Robotics Documentation',
      description: 'Comprehensive documentation and observation of AI-driven robotic systems in Honda\'s large-scale manufacturing facility.',
      challenge: 'Understanding complex integration of robotics, AI, and safety protocols in high-volume automotive production.',
      solution: 'Shadowed senior engineers, documented workflow processes, and studied how AI-driven robots improve precision and efficiency in assembly lines.',
      technologies: ['Industrial Robotics', 'AI Automation', 'Safety Systems', 'Documentation'],
      outcomes: [
        'Gained exposure to world-class manufacturing processes',
        'Documented operational safety protocols',
        'Understood real-time system responsiveness',
        'Learned about industrial-scale automation',
      ],
      icon: 'robotics',
      color: 'var(--color-accent-7)',
    },
  ];

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold"
            style={{ color: 'var(--color-heading)' }}
          >
            Project Highlights
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Key projects and deliverables from internship experiences
          </p>
        </div>

        {/* Projects Grid */}
        <div className="space-y-8">
          {projects.map((project, idx) => (
            <div
              key={project.id}
              className="rounded-xl overflow-hidden transition-all duration-300 hover:shadow-card-hover"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 sm:p-6 lg:p-8">
                {/* Left Column - Project Info */}
                <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                  {/* Header */}
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: 'var(--color-theme-2)',
                        borderColor: 'var(--color-border-light)',
                        borderWidth: '1px',
                        borderStyle: 'solid'
                      }}
                    >
                      <Icon name={project.icon} size="2xl" className="text-4xl" style={{ color: project.color }} />
                    </div>
                    <div className="flex-1">
                      <div
                        className="text-xs sm:text-sm font-semibold mb-1"
                        style={{ color: project.color }}
                      >
                        {project.company}
                      </div>
                      <h3
                        className="text-xl sm:text-2xl font-bold"
                        style={{ color: 'var(--color-heading)' }}
                      >
                        {project.title}
                      </h3>
                      <p
                        className="text-sm sm:text-base mt-2"
                        style={{ color: 'var(--color-subtle)' }}
                      >
                        {project.description}
                      </p>
                    </div>
                  </div>

                  {/* Challenge & Solution */}
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <h4
                        className="text-sm font-semibold mb-2"
                        style={{ color: 'var(--color-subheading)' }}
                      >
                        Challenge
                      </h4>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: 'var(--color-subtle)' }}
                      >
                        {project.challenge}
                      </p>
                    </div>
                    <div>
                      <h4
                        className="text-sm font-semibold mb-2"
                        style={{ color: 'var(--color-subheading)' }}
                      >
                        Solution
                      </h4>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: 'var(--color-subtle)' }}
                      >
                        {project.solution}
                      </p>
                    </div>
                  </div>

                  {/* Technologies */}
                  <div>
                    <h4
                      className="text-sm font-semibold mb-3"
                      style={{ color: 'var(--color-subheading)' }}
                    >
                      Technologies Used
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, techIdx) => (
                        <span
                          key={techIdx}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium"
                          style={{
                            backgroundColor: 'var(--color-theme-2)',
                            color: 'var(--color-card-text)',
                            borderColor: 'var(--color-border-light)',
                            borderWidth: '1px',
                            borderStyle: 'solid'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* View Documentation Button */}
                  {project.documentation && (
                    <div>
                      <a
                        href={project.documentation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center py-2 px-6 rounded-lg text-sm font-medium transition-all duration-200"
                        style={{
                          backgroundColor: 'var(--color-button-primary-bg)',
                          color: 'var(--color-button-primary-text)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-button-primary-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-button-primary-bg)';
                        }}
                      >
                        View Project Documentation
                      </a>
                    </div>
                  )}
                </div>

                {/* Right Column - Outcomes */}
                <div
                  className="rounded-xl p-6"
                  style={{
                    backgroundColor: 'var(--color-theme-2)',
                    borderColor: 'var(--color-border-light)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <h4
                    className="text-sm font-semibold mb-4"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    Key Outcomes
                  </h4>
                  <ul className="space-y-3">
                    {project.outcomes.map((outcome, outcomeIdx) => (
                      <li
                        key={outcomeIdx}
                        className="text-sm flex items-start"
                        style={{ color: 'var(--color-subtle)' }}
                      >
                        <span
                          className="mr-2 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: project.color }}
                        ></span>
                        <span>{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectHighlightsSection;
